import React, { useState } from 'react';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useClerk, useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import CgPopover from 'modules/Common/ui/CgPopover';
import { PRIMARY_COLOR } from 'modules/Common/utils/brandColor';

/**
 * UserMenu — the app's single account menu, and the only place a user can
 * sign out.
 *
 * Replaces Clerk's hosted <UserButton>, which rendered as a bare unlabelled
 * avatar: no label, no chevron, no hover state, and "Sign out" buried one
 * click deep behind a control that gave no sign it was a control. See
 * docs/features/logout-discoverability/ for the plan and the locked decisions
 * referenced below.
 *
 * Two rules this component must keep:
 *
 *  - It never tears anything down itself (invariant). `clerk.signOut()` only.
 *    Clearing `cg_backend_token` and the react-query cache belongs to the
 *    session listener in _app.jsx, which is the single logout boundary — a
 *    second teardown path here would silently diverge from it.
 *  - It never names a post-sign-out destination (D3). No `redirectUrl`, no
 *    `afterSignOutUrl`: the <SignedOut> gate in _app.jsx redirects, which
 *    keeps sign-out a pure SPA transition. Passing a URL would hard-navigate
 *    and reload the app, which is exactly the divergence that let the diagram
 *    ribbon land somewhere different from the sidebar.
 *
 * Variants: default (expanded sidebar rail — full row with name and email),
 * `isCollapsed` (56px rail — avatar only, panel to the right), and `compact`
 * (dense chrome like the diagram ribbon — avatar only, panel below).
 */

const userInitials = (user) => {
  const from = (s) => (s ? s.trim()[0]?.toUpperCase() : '');
  const initials = `${from(user?.firstName)}${from(user?.lastName)}`;
  if (initials) return initials;
  const email = user?.primaryEmailAddress?.emailAddress;
  return from(email) || '?';
};

/**
 * Round initials chip, deliberately not a reuse of the sidebar's OrgAvatar:
 * that one is a rounded *square* keyed on org id for its palette swatch.
 * People are round and take the brand fill; organizations are square and take
 * their own color. Keeping them visually distinct is what lets the two footer
 * rows read as different kinds of thing.
 */
const UserAvatar = ({ user, size = 28 }) => {
  if (user?.hasImage && user?.imageUrl) {
    return (
      <img
        src={user.imageUrl}
        alt=""
        className="shrink-0 rounded-full object-cover bg-slate-100"
        style={{ width: size, height: size }}
        aria-hidden="true"
      />
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center font-semibold text-white shrink-0 rounded-full"
      style={{
        width: size,
        height: size,
        background: PRIMARY_COLOR,
        fontSize: Math.max(10, size * 0.38),
      }}
      aria-hidden="true"
    >
      {userInitials(user)}
    </span>
  );
};

/**
 * The popover body. Exported separately from the container so unit tests can
 * assert on it directly — Radix needs real pointer events to open, which jsdom
 * doesn't dispatch faithfully (same reasoning as NavLanguageSwitcher.test.jsx).
 *
 * CgPopover wraps its content in `px-3 py-2`; `-mx-3 -my-2` cancels that so
 * rows can run edge to edge, the same idiom OrgSwitcherMenu uses.
 */
export const UserMenuPanel = ({ user, onAccountSettings, onSignOut, signingOut }) => {
  const { t } = useTranslation('app');
  const email = user?.primaryEmailAddress?.emailAddress;

  return (
    <div className="-mx-3 -my-2 w-[248px] py-1">
      <div className="px-3 pt-2 pb-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 m-0">
          {t('nav.account.heading', 'Account')}
        </p>
        {email && <p className="text-[11px] text-slate-500 m-0 truncate">{email}</p>}
      </div>

      <button
        type="button"
        onClick={onAccountSettings}
        className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-slate-600 hover:bg-slate-50 transition-colors"
      >
        <FontAwesomeIcon
          icon={faIcons.faUserGear}
          className="text-slate-400 text-[12px] w-3.5"
        />
        {t('nav.account.settings', 'Account settings')}
      </button>

      <div className="h-px bg-slate-100 mx-2 my-1" />

      <button
        type="button"
        onClick={onSignOut}
        disabled={signingOut}
        className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        <FontAwesomeIcon
          icon={signingOut ? faIcons.faSpinner : faIcons.faRightFromBracket}
          className={`text-[12px] w-3.5 ${signingOut ? 'animate-spin' : ''}`}
        />
        {signingOut
          ? t('nav.account.signingOut', 'Signing out…')
          : t('nav.account.signOut', 'Sign out')}
      </button>
    </div>
  );
};

const UserMenu = ({ isCollapsed = false, compact = false }) => {
  const { t } = useTranslation('app');
  const { user } = useUser();
  const clerk = useClerk();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const name = user?.fullName || t('nav.orgSwitcher.you', 'You');
  const email = user?.primaryEmailAddress?.emailAddress;
  const menuLabel = t('nav.account.ariaLabel', 'Account menu');
  // Icon-only triggers get the identity as their tooltip, falling back to the
  // menu's own name so the collapsed rail is never a mystery circle — the
  // exact bug this component exists to fix.
  const iconTitle = user?.fullName || email || t('nav.account.heading', 'Account');
  const iconOnly = isCollapsed || compact;

  const handleAccountSettings = () => {
    setOpen(false);
    clerk?.openUserProfile?.();
  };

  // D7: one sign-out per open. Clerk's signOut is async and the popover stays
  // mounted while it resolves, so an unguarded handler fires two teardowns on
  // a double click. No `finally` re-enable either: on success the tree
  // unmounts behind the <SignedOut> gate, and flipping back to "Sign out"
  // mid-teardown just flashes the label.
  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await clerk.signOut();
    } catch {
      // Offline, or Clerk unreachable. Re-arm rather than stranding the user
      // on a permanently disabled row.
      setSigningOut(false);
      setOpen(false);
    }
  };

  const triggerElement = iconOnly ? (
    <button
      type="button"
      title={iconTitle}
      aria-label={menuLabel}
      className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 transition-colors"
    >
      <UserAvatar
        user={user}
        size={26}
      />
    </button>
  ) : (
    <button
      type="button"
      aria-label={menuLabel}
      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 transition-colors"
    >
      <UserAvatar
        user={user}
        size={28}
      />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="text-[12px] font-medium text-slate-900 m-0 truncate">{name}</p>
        <p className="text-[10px] text-slate-400 m-0 truncate">{email || ''}</p>
      </div>
      <FontAwesomeIcon
        icon={faIcons.faChevronUp}
        className="text-slate-400 text-[10px] shrink-0"
      />
    </button>
  );

  return (
    <CgPopover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      // Collapsed rail opens sideways; the ribbon sits at the top of the
      // viewport so it opens downward; the expanded footer opens upward.
      position={compact ? 'bottom' : isCollapsed ? 'right' : 'top'}
      // Both values look wrong and are not. CgPopover always renders a
      // Popover.Arrow, and Radix adds that arrow's allowance on top of
      // sideOffset — so the app's usual `sideOffset={6}` leaves the panel 18px
      // from its trigger. The arrow is white-filled on a white sidebar, i.e.
      // invisible, so that space reads as the menu floating loose instead of
      // belonging to the row that opened it.
      //
      // The allowance is not the same on every side (measured, not derived:
      // ~12px above the trigger, ~5.5px beside and below it), so one offset
      // cannot give one tether. These two land every variant at ~6px.
      sideOffset={iconOnly ? 0 : -6}
      triggerElement={triggerElement}
      popoverContent={
        <UserMenuPanel
          user={user}
          onAccountSettings={handleAccountSettings}
          onSignOut={handleSignOut}
          signingOut={signingOut}
        />
      }
    />
  );
};

export default UserMenu;
